import React, { FunctionComponent, useEffect, useState, useMemo } from 'react'
import { useIntercom } from 'react-use-intercom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, FieldValues } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import Alert from 'Shared/components/Alert'
import { getUserId } from 'Redux/selectors/auth'
import { sendReview } from 'Redux/actions/reviewsActions'
import WarningSvg from 'Shared/img/Warning.svg'
import ErrorSvg from 'Shared/img/svg/errorAlertIcon.svg'
import { reviewTrackSchema } from 'Helpers/validation'
import {
  CampaignPlatformTypeEnum,
  ReviewSelectValue,
  ReviewType,
} from 'Types/index'
import { reviewMadeEvent, trackAddedToPlaylistEvent } from 'Helpers/tagManager'
import { getUserInfo } from 'Redux/actions/auth'

import { SelectPlaylist } from '../SelectPlaylist'

import { ReviewFormValues, ReviewTrackFormProps } from './props'
import {
  Container,
  SectionTitle,
  SectionTitle2,
  TextArea,
  ButtonsContainer,
  Button,
  ButtonSimple,
  Warning,
  WarningIcon,
  WarningText,
} from './styles'

export const ReviewTrackForm: FunctionComponent<ReviewTrackFormProps> = ({
  playlistFromWarning,
  setModalHeaderGreen,
  setThreeReviewsWarning,
  onSuccess,
  reviewDetails,
  openSkipModal,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { show } = useIntercom()
  const [notAuthorized, setNotAuthorized] = useState(false)
  const [errorOnSend, setErrorOnSend] = useState(false)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const userId = useSelector(getUserId)
  const blacklistWords = useSelector(
    (state: RootStateOrAny) => state.reviews.wordsBlacklist
  )

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm<FieldValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: yupResolver<yup.AnyObjectSchema>(
      reviewTrackSchema(blacklistWords)
    ),
  })

  const values = getValues()
  const addAction = values?.reviewPlaylistSelect?.type === 'add'
  const rejectionAction = values?.reviewPlaylistSelect?.type === 'reject'
  const actionId = values?.reviewPlaylistSelect?.id

  const reason = useMemo(() => {
    if (rejectionAction && actionId === 1)
      return 'I do not like the track enough'

    if (rejectionAction && actionId === 2)
      return 'The track does not fit my playlist genre'
    return ''
  }, [rejectionAction, actionId, addAction])

  const watchComment = watch('reviewText')

  const watchSelect: ReviewSelectValue = watch('reviewPlaylistSelect')

  useEffect(() => {
    if (
      watchSelect?.type === ReviewType.reject &&
      reviewDetails.isRejectLastThree
    ) {
      setModalHeaderGreen(false)
      setThreeReviewsWarning(true)
    }
  }, [watchSelect])

  useEffect(() => {
    if (playlistFromWarning) {
      setValue('reviewPlaylistSelect', playlistFromWarning, {
        shouldValidate: true,
      })
    }
  }, [playlistFromWarning])

  const onSubmit = (values: ReviewFormValues) => {
    setSubmitDisabled(true)
    dispatch(
      sendReview(
        reviewDetails.campaign_id,
        values.reviewText,
        values.reviewPlaylistSelect,
        (msg: string, error: { response: { status: number } }) => {
          if (error?.response?.status === 403) {
            setNotAuthorized(true)
            setSubmitDisabled(false)
            return
          }
          if (error?.response?.status === 404) {
            setErrorOnSend(true)
            return
          }

          dispatch(getUserInfo())

          if (!msg) {
            setNotAuthorized(false)
            onSuccess()
            return
          }
          setNotAuthorized(false)
          onSuccess()
        }
      )
    )
    addAction &&
      trackAddedToPlaylistEvent(
        reviewDetails.campaign_id,
        CampaignPlatformTypeEnum.SPOTIFY,
        userId,
        Number(reviewDetails.id),
        Number(reviewDetails.potential_reward),
        Number(reviewDetails.track.id),
        false
      )
    reviewMadeEvent(
      reviewDetails.campaign_id,
      CampaignPlatformTypeEnum.SPOTIFY,
      addAction,
      userId,
      Number(reviewDetails.id),
      Number(reviewDetails.potential_reward),
      !addAction && actionId,
      reason,
      false
    )
  }

  const [handleSendReview] = useDebouncedCallback(onSubmit, 1000)

  return (
    <Container onSubmit={handleSubmit(handleSendReview)}>
      <SectionTitle>{t('reviewsPage.addToPlaylist')}</SectionTitle>
      <Controller
        control={control}
        name="reviewPlaylistSelect"
        render={({ field: { onChange, value, ref } }) => (
          <SelectPlaylist ref={ref} value={value} onChange={onChange} />
        )}
      />
      <SectionTitle2>{t('reviewsPage.writeReview')}</SectionTitle2>
      <Controller
        control={control}
        name="reviewText"
        render={({ field }) => (
          <TextArea
            {...field}
            placeholder={t('reviewsPage.commentPlaceholder')}
            onCopy={e => {
              e.preventDefault()
              setError('reviewText', {
                message: t('reviewsPage.uniqueReviewRequired'),
              })
              return false
            }}
            onCut={e => {
              e.preventDefault()
              setError('reviewText', {
                message: t('reviewsPage.uniqueReviewRequired'),
              })
              return false
            }}
            onPaste={e => {
              e.preventDefault()
              setError('reviewText', {
                message: t('reviewsPage.uniqueReviewRequired'),
              })
              return false
            }}
          />
        )}
      />
      {errors.reviewText && !errorOnSend && (
        <Warning>
          <WarningIcon src={WarningSvg} />
          <WarningText>{errors?.reviewText?.message}</WarningText>
        </Warning>
      )}
      {errorOnSend && (
        <Warning>
          <WarningIcon src={ErrorSvg} />
          <WarningText red>
            {t('reviewsPage.errorWhileSendingReview')}
          </WarningText>
        </Warning>
      )}
      <ButtonsContainer>
        {reviewDetails.daysLeft > 5 && (
          <ButtonSimple
            disabled={submitDisabled}
            onClick={() => {
              openSkipModal({
                review_id: reviewDetails.id,
                campaign_id: reviewDetails.campaign_id,
              })
            }}
          >
            {t('reviewsPage.skipReview')}
          </ButtonSimple>
        )}
        {!errorOnSend ? (
          <Button
            disabled={!watchSelect || !watchComment || submitDisabled}
            type="submit"
          >
            {t('reviewsPage.sendReview')}
          </Button>
        ) : (
          <Button type="button" onClick={show}>
            {t('reviewsPage.contactSupport')}
          </Button>
        )}
      </ButtonsContainer>
      {notAuthorized && (
        <Alert color="danger" icon>
          <div className="alert__text">
            <div className="alert__title">
              {t('reviewsPage.unathorizedMessage')}
            </div>
          </div>
        </Alert>
      )}
    </Container>
  )
}
